import React, { Component, lazy } from 'react';
import ReactDOM from 'react-dom';
import { observable, action, makeObservable, runInAction } from 'mobx';
import { observer } from 'mobx-react';

import type { Message, Model, AttachmentMeta, ImageEstimatesApiResponse, ImageModelEstimateResponse } from '@root/models';
import { getAttachmentIconComponent } from '@root/models';
import { modalService } from '@components/Modal';

import { PromiseCompletion } from '@classes/PromiseCompletion';
import { helperService } from '@services/helperService';
import modelService, { resolveLoadedModelName } from '@services/modelService';
import { wsService } from '@services/wsService';
import { imageService } from '@services/imageService';
import { toastService } from '@services/toastService';
import apiService from '@services/apiService';

import { ImgAttach } from '@images/ImgAttach';
import { ImgCancel } from '@images/ImgCancel';
import { ImgStop } from '@images/ImgStop';
import { ImgReturn } from '@images/ImgReturn';
import { ImgDocumentCheck } from '@images/ImgDocumentCheck';
import { ImgWarning } from '@images/ImgWarning';
import { ImgMusic } from '@images/ImgMusic';
import { ImgNewImage } from '@images/ImgNewImage';
import { ImgCollapse } from '@images/ImgCollapse';
import { ImgExpand } from '@images/ImgExpand';
import { ImgMagic } from '@images/ImgMagic';
import { ImgTools } from '@images/ImgTools';
import { ImgFileDoc } from '@images/ImgFileDoc';
import { ImgFileDocx } from '@images/ImgFileDocx';
import { ImgFilePdf } from '@images/ImgFilePdf';
import { ImgFileTxt } from '@images/ImgFileTxt';
import { ImgFileCsv } from '@images/ImgFileCsv';
import { ImgFileHtml } from '@images/ImgFileHtml';
import { ImgFileSvg } from '@images/ImgFileSvg';
import { ImgFileSql } from '@images/ImgFileSql';
import { ImgFileLog } from '@images/ImgFIleLog';
import { ImgFileGeneric } from '@images/ImgFileGeneric';
import { ImageWrapper } from '@components/ImageWrapper';
import MemoryDistributionPopup from '@components/MemoryDistributionPopup';
import { Loader } from '@components/Loader';
import { UploadProgressRing } from '@components/UploadProgressRing';
import ToggleModeButton from '@components/ToggleModeButton';
import TokensCounter from '@components/TokensCounter';
import VoiceRecorder from '@components/MicToText';
import ImageLayoutPicker, { getLayoutIcon, type ImageLayout, type ImageSizeChoice } from './ImageLayoutPicker';
import ImageModelPicker, { type ImageGenModel } from './ImageModelPicker';
import ImageStylePicker, { parseLoraStyleName, normalizeStyleSelections } from './ImageStylePicker';
import type { ImageStyleSelection } from './ImageStylePicker';
import VideoModelPicker, { type VideoGenModel } from './VideoModelPicker';
import ContextMenu, { ContextMenuItem } from '@components/ContextMenu/index.ts';
import { ImgImage } from '@components/Images/ImgImage';
import { ImgCamera } from '@components/Images/ImgCamera';
import { ImgGallerySelect } from '@components/Images/ImgGallerySelect';
import { ImgKeyArrow } from '@components/Images/ImgKeyArrow';
import { ImgImageMode } from '@components/Images/ImgImageMode';
import { ImgVideoMode } from '@components/Images/ImgVideoMode';
import skillService, { type SkillDto } from '@services/skillService';
import SkillMentionMenu from './SkillMentionMenu';
const TextViewerDialog = lazy(() => import("./TextViewerDialog"));
const PdfViewerDialog = lazy(() => import("./PdfViewerDialog"));
const ImageGalleryPicker = lazy(() => import('./ImageGallery'));

type PromptAreaProps = {
  chatId: string | null;
  isGenerating: boolean;
  isVisible?: boolean;
  currentModel: Model | null;
  isAlternativeMode: boolean;
  hasSystemMessage: boolean;
  tokensInConversation: number;
  predictLength?: string | null;

  showTokenUsage: boolean;
  tools: string[] | null;
  conversation?: Message[];
  /** User's available skills, used to populate the "/" slash-command menu. */
  skills: SkillDto[];
  onModeSwitch: (isAlternativeMode: boolean) => void;
  onSendMessage: (message: string, attachmentIds: string[], startFromBeginning: boolean) => void;
  /** Queue a steering ("upcoming") message while the model is generating. */
  onSteerMessage?: (message: string) => void;
  onImageGenerate: (imageIds: string[], promptPositive: string, promptNegative: string, model: ImageGenModel, layout: ImageLayout, styles: ImageStyleSelection[], width?: number, height?: number, transparent?: boolean) => void;
  onVideoGenerate: (imageIds: string[], prompt: string, model: VideoGenModel, layout: ImageLayout, size: number, fps: number, length: number) => void;
  onCancelRequest: () => void;
  onOpenToolsConfig: () => void;
  onImageGenModeChange?: (active: boolean) => void;
  /** Called when the user picks a skill from the "/" slash-command menu. */
  onApplySkill: (skill: SkillDto) => void;
};

@observer
class PromptArea extends Component<PromptAreaProps> {
  /** Read stored selected styles, migrating the legacy plain-string format to { name, strength }. */
  private static _loadStoredStyles(): ImageStyleSelection[] {
    const stored = localStorage.getItem('imageGenStyles');
    if (!stored) return [];
    try {
      return normalizeStyleSelections(JSON.parse(stored));
    } catch {
      return [];
    }
  }

  @observable inputValue: string;
  @observable private _fileRef: React.RefObject<HTMLInputElement | null> = React.createRef();
  @observable private _docFileRef: React.RefObject<HTMLInputElement | null> = React.createRef();
  @observable private _allTypesRef: React.RefObject<HTMLInputElement | null> = React.createRef();
  @observable private _cameraRef: React.RefObject<HTMLInputElement | null> = React.createRef();
  private _attachBtnRef: React.RefObject<HTMLButtonElement | null> = React.createRef();
  @observable private _textareaRef: React.RefObject<HTMLTextAreaElement | null> = React.createRef();
  @observable private _imageMetas: AttachmentMeta[] = [];
  @observable private _showGalleryPicker: boolean = false;
  @observable private _attachmentMetas: AttachmentMeta[] = [];
  @observable private _messageTokens: number | null = null;
  @observable private _isShiftPressed: boolean = false;
  @observable private _isCtrlPressed: boolean = false;
  @observable private _hasKeyboard: boolean = false;
  @observable private _isDragOver: boolean = false;
  @observable private _isVoiceRecording: boolean = false;
  /** Aggregates all in-flight attachment uploads; `isPending` is true while any file is
   *  still uploading (gates the send button + the Enter key). */
  @observable private _uploading: PromiseCompletion = new PromiseCompletion();
  /** Per-file placeholders for attachments still in flight (server id unknown yet).
   *  `progress` is the upload fraction (0-1) shown on the placeholder's progress ring. */
  @observable private _pendingUploads: { id: string; filename: string; isImage: boolean; progress: number }[] = [];
  /** Abort controllers for in-flight uploads, keyed by pending id (cancel-on-click). */
  private _uploadAborts = new Map<string, AbortController>();
  private _uploadSeq = 0;
  /** Ids of attached images whose composer-held ref is still an unbound draft (freshly
   *  uploaded, never sent with a generation). When such an image is kept in the composer
   *  after a generation, its draft ref was transferred to the generated user message —
   *  a compensating addRef is fired so the composer's hold is backed by a real server ref. */
  private _unboundImageIds = new Set<string>();
  @observable private _keywordSuggestion: string | null = null;
  /** Shown when an i2i/i2v suggestion was selected without a source image:
   *  the prompt transforms an image, so the user needs to attach one. Cleared once an image is attached. */
  @observable private _imageRequiredHint = false;
  private _generatePromptCompletion = new PromiseCompletion();
  private _keywordSuggestionTimer: ReturnType<typeof setTimeout> | null = null;
  private _maxImagesErrorShownAt: number = 0;
  /** Non-null (holds the text typed after "/") while the skill slash-command menu is open. */
  @observable private _slashQuery: string | null = null;
  @observable private _slashHighlightIndex: number = 0;
  /** Caret offset of the triggering "/" character within inputValue. */
  private _slashStartPos: number = 0;

  /** Dimensions of the first attached image, used for the 'as-image' layout. */
  private get _firstImageDimensions(): { width: number; height: number } | null {
    const first = this._imageMetas[0];
    if (!first || first.imageWidth == null || first.imageHeight == null) return null;
    return { width: first.imageWidth, height: first.imageHeight };
  }

  // Image generation mode
  @observable private _isImageGenMode: boolean = localStorage.getItem('isImageGenMode') === 'true';
  @observable private _isVideoGenMode: boolean = localStorage.getItem('isVideoGenMode') === 'true';
  @observable private _imageLayout: ImageLayout = (() => {
    const stored = localStorage.getItem('imageLayout') as ImageLayout;
    const valid: ImageLayout[] = ['wide', 'tall', 'landscape', 'square', 'portrait', 'as-image'];
    // 'as-image' needs an attached image, and the composer's images are never
    // persisted — a stored 'as-image' is always stale at startup → fall back to square.
    if (stored === 'as-image') { localStorage.setItem('imageLayout', 'square'); return 'square'; }
    return valid.includes(stored) ? stored : 'square';
  })();
  @observable private _imageSize: ImageSizeChoice = (() => {
    const s = localStorage.getItem('imageSize') as ImageSizeChoice;
    const valid: ImageSizeChoice[] = ['1024', '1536', '2048', 'as-image'];
    // 'as-image' needs an attached image (never persisted) — stale at startup → fall back to 1K.
    if (s === 'as-image') { localStorage.setItem('imageSize', '1024'); return '1024'; }
    if (!valid.includes(s)) return '1024';
    // Guests are limited to 1K (and as-image); 1.5K/2K need an account.
    if ((s === '1536' || s === '2048') && (window.user?.role ?? 'guest') === 'guest') return '1024';
    return s;
  })();
  @observable private _imageGenModel: ImageGenModel = (() => {
    const stored = localStorage.getItem('imageGenModel');
    if (stored === 'qwen-image-2512' || stored === 'qwen-image-edit-2511') return 'qwen';
    return (stored as ImageGenModel) || 'zImage';
  })();
  @observable private _videoGenModel: VideoGenModel = (localStorage.getItem('videoGenModel') as VideoGenModel) || 'Wan2.2';
  @observable private _videoSize: number = (() => {
    const s = parseInt(localStorage.getItem('videoSize') || '480');
    if (![480, 640, 720].includes(s)) return 480;
    // Guests are limited to 480p; 640p/720p need an account.
    if ((s === 640 || s === 720) && (window.user?.role ?? 'guest') === 'guest') return 480;
    return s;
  })();
  @observable private _videoFps: number = (() => {
    const f = parseInt(localStorage.getItem('videoFps') || '16');
    return [8, 12, 16, 24].includes(f) ? f : 16;
  })();
  @observable private _videoLength: number = (() => {
    const l = parseInt(localStorage.getItem('videoLength') || '7');
    return [3, 5, 7, 10, 12, 15].includes(l) ? l : 7;
  })();
  @observable private _selectedStyles: ImageStyleSelection[] = PromptArea._loadStoredStyles();
  @observable private _imageGenControlsExpanded: boolean = localStorage.getItem('imageGenControlsExpanded') !== 'false';
  // Only mount the image-gen controls (incl. model preview images) while the panel is
  // visible, so they are lazy-loaded and not fetched on the start page. A short delay
  // keeps them mounted through the collapse animation before unmounting.
  @observable private _imageGenControlsMounted: boolean = localStorage.getItem('isImageGenMode') === 'true';
  private _imageGenUnmountTimer: ReturnType<typeof setTimeout> | null = null;
  @observable private _imageEstimates: ImageEstimatesApiResponse | null = null;
  /** Admin-only memory popup for the image warning badge. */
  @observable private _showMemoryPopup: boolean = false;
  private _memoryBadgeRef = React.createRef<HTMLSpanElement>();

  /** Map of image gen model name → workflowType for estimate lookup */
  private static readonly MODEL_TO_WORKFLOW: Record<string, string> = {
    'zImage': 'zimage',
    'SDXL': 'sdxl',
    'Flux1.Dev': 'flux',
    'Flux.Kontext': 'flux-kontext',
    'Flux2.Dev': 'flux2',
    'Flux2.Klein': 'flux2-klein',
    'qwen': 'qwen-image-edit',
    'qwen2.1': 'qwen-image-2.1',
    'qwen2.1t': 'qwen-image-2.1',
    'krea2': 'krea2',
  };

  /** i2i model → workflowType that carries the maxImages limit */
  private static readonly I2I_TO_WORKFLOW: Record<string, string> = {
    'Flux.Kontext': 'flux-kontext',
    'Flux2.Dev': 'flux2',
    'Flux2.Klein': 'flux2-klein',
    'qwen': 'qwen-image-edit',
    'qwen2.1': 'qwen-image-2.1-edit',
    'qwen2.1t': 'qwen-image-2.1-edit',
    'krea2': 'krea2',
  };

  /** Current estimate for the selected image model, or null if not available */
  private get _currentEstimate(): ImageModelEstimateResponse | undefined {
    if (!this._imageEstimates?.estimates.length) return undefined;
    const workflowType = PromptArea.MODEL_TO_WORKFLOW[this._imageGenModel];
    if (!workflowType) return undefined;
    
    // Find the estimate for this workflow type
    const est = this._imageEstimates.estimates.find(e => e.workflowType === workflowType);
    if (!est) return undefined;

    // Available VRAM is computed server-side from the live free GPU memory.
    // Loaded Ollama models are evictable to make room for a ComfyUI payload, so
    // they are NOT subtracted. Unknown → assume it fits (no warning shown).
    const availableVramGb = this._imageEstimates.availableVramGb;

    return {
      ...est,
      canRun: availableVramGb !== undefined ? est.sizeGb <= availableVramGb : true,
      availableVramGb
    };
  }

  private _prefetchModel?: (() => void) & { cancel: () => void };

  private get _isMobile(): boolean {
    return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  }

  /** Higher resolution tiers (1.5K/2K images, 640p/720p video) need an account; guests get 1K/480p. */
  private get _canUseHighRes(): boolean {
    return (window.user?.role ?? 'guest') !== 'guest';
  }

  /** Disable state for the Send/Generate button.
   *  Chat mode requires the chat model to be usable (completion capability, not
   *  disabled for memory). Image/video gen mode is ComfyUI-backed and never uses
   *  the chat model, so those checks don't apply — VRAM fit is shown via the
   *  estimate badge instead. */
  private get _isSendDisabled(): boolean {
    if (this._uploading.isPending || this._generatePromptCompletion.isPending) return true;
    if (wsService.connectionState !== 'connected') return true;
    if (this._isImageGenMode) return false;
    const capabilities = this.props.currentModel?.capabilities || [];
    return !capabilities.includes('completion') || !!this.props.currentModel?.isDisabled;
  }

  /** True when the current gen mode accepts an image attachment (video i2v, or image-edit models) */
  private get _imageAttachEnabled(): boolean {
    if (this._isVideoGenMode) return true;
    return this._imageGenModel === 'Flux.Kontext' ||
           this._imageGenModel === 'Flux2.Dev' ||
           this._imageGenModel === 'Flux2.Klein' ||
           this._imageGenModel === 'qwen' ||
           this._imageGenModel === 'qwen2.1' ||
           this._imageGenModel === 'qwen2.1t' ||
           this._imageGenModel === 'krea2';
  }

  /** Maximum number of reference images for the current model (0 = t2i, no attachment) */
  private get _maxImages(): number {
    if (this._isVideoGenMode) return 1;
    const workflowType = PromptArea.I2I_TO_WORKFLOW[this._imageGenModel];
    if (!workflowType) return 0;
    // Prefer the server-provided limit; fall back to the legacy hardcoded cap.
    const est = this._imageEstimates?.estimates.find(e => e.workflowType === workflowType);
    return est?.maxImages ?? 3;
  }

  /** Display name for the current image gen model */
  private _getModelDisplayName(model: ImageGenModel): string {
    const map: Record<string, string> = {
      'zImage': 'zImage',
      'SDXL': 'SDXL',
      'Flux1.Dev': 'Flux.1 Dev',
      'Flux.Kontext': 'Flux Kontext',
      'Flux2.Dev': 'Flux.2 Dev',
      'Flux2.Klein': 'Flux.2 Klein',
      'qwen': 'Qwen Image',
      'qwen2.1': 'Qwen 2.1',
      'qwen2.1t': 'Qwen 2.1 T',
      'krea2': 'Krea2',
    };
    return map[model] || model;
  }

  /** Compute output size from the chosen size + layout ratio, capped at model max area, snapped to 16px. */
  private get _outputSize(): { width: number; height: number } | null {
    // Max pixel area per model (based on official recommendations)
    let maxPixels: number;
    switch (this._imageGenModel) {
      case 'SDXL':        maxPixels = 1024 * 1024; break;       // 1.05 MP (1024×1024)
      case 'Flux1.Dev':
      case 'Flux.Kontext':
      case 'qwen':
      default:            maxPixels = 2 * 1024 * 1024; break;   // 2 MP
      case 'qwen2.1':
      case 'qwen2.1t':
      case 'krea2':
      case 'zImage':
      case 'Flux2.Dev':
      case 'Flux2.Klein': maxPixels = 4 * 1024 * 1024; break;   // 4 MP (2048×2048)
    }

    let baseW: number, baseH: number;

    if (this._imageLayout === 'as-image' || this._imageSize === 'as-image') {
      const dims = this._firstImageDimensions;
      if (!dims) return null;
      baseW = dims.width;
      baseH = dims.height;
    } else {
      const base = parseInt(this._imageSize);
      const ratio = this._imageLayout === 'landscape' ? 3 / 2
        : this._imageLayout === 'portrait' ? 2 / 3
        : this._imageLayout === 'wide' ? 16 / 9
        : this._imageLayout === 'tall' ? 9 / 16
        : 1;
      // `base` is always the LONGEST side; the short side = base * ratio when
      // ratio < 1 (multiply — dividing would invert the layout, e.g. 2K portrait
      // became 3641×2048, then the area cap squashed it to 2736×1536).
      if (ratio >= 1) {
        baseW = base;
        baseH = Math.round(base / ratio);
      } else {
        baseH = base;
        baseW = Math.round(base * ratio);
      }
    }

    // Cap at model max area
    let finalW = baseW;
    let finalH = baseH;
    const area = finalW * finalH;
    if (area > maxPixels) {
      const scale = Math.sqrt(maxPixels / area);
      finalW = Math.round(finalW * scale);
      finalH = Math.round(finalH * scale);
    }

    // Snap to nearest 16 (latent-space requirement)
    finalW = Math.max(64, Math.round(finalW / 16) * 16);
    finalH = Math.max(64, Math.round(finalH / 16) * 16);

    return { width: finalW, height: finalH };
  }
  
  constructor(props: PromptAreaProps) {
    super(props);
    this.inputValue = localStorage.getItem('promptInput_' + (props.chatId || 'new')) || '';
    makeObservable(this);
  }

  componentDidMount(): void {
    setTimeout(this._recalculateHeight, 0);
    
    this._prefetchModel = helperService.throttle(this._doPrefetchModel, 5000);

    window.addEventListener('keydown', this._onGlobalKeyDown);
    window.addEventListener('keyup', this._onGlobalKeyUp);

    // Fetch image model VRAM estimates only when image-gen mode is active,
    // so the start page (image mode off) doesn't issue a wasted request.
    // Entering image mode later triggers a refetch in _onToggleImageGenMode.
    if (this._isImageGenMode) {
      this._fetchImageEstimates();
    }
  }

  componentWillUnmount(): void {
    this._prefetchModel?.cancel();
    if (this._keywordSuggestionTimer !== null) clearTimeout(this._keywordSuggestionTimer);
    if (this._imageGenUnmountTimer !== null) clearTimeout(this._imageGenUnmountTimer);
    window.removeEventListener('keydown', this._onGlobalKeyDown);
    window.removeEventListener('keyup', this._onGlobalKeyUp);
  }

  componentDidUpdate(prevProps: PromptAreaProps) {
    if (prevProps.chatId !== this.props.chatId) {
      if (this._isImageGenMode) {
        // In image mode preserve the current prompt and save it under the new chat ID
        // so users can iterate on the prompt across generations
        this.save();
      } else {
        this.inputValue = localStorage.getItem('promptInput_' + (this.props.chatId || 'new')) || '';
        setTimeout(this._recalculateHeight, 0);
      }
    }
    
    // Recalculate VRAM estimates when the selected chat model changes
    if (prevProps.currentModel?.name !== this.props.currentModel?.name || 
        prevProps.currentModel?.server !== this.props.currentModel?.server) {
      if (this._isImageGenMode) {
        this._fetchImageEstimates();
      }
    }
  }

  @action
  private _onGlobalKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Shift') {
      this._isShiftPressed = true;
      this._hasKeyboard = true;
      e.preventDefault();
    } else if (e.key === 'Control' || e.key === 'Meta') {
      this._isCtrlPressed = true;
      this._hasKeyboard = true;
      e.preventDefault();
    } 
  };

  @action
  private _onGlobalKeyUp = (e: KeyboardEvent) => {
    if (e.key === 'Shift') {
      this._isShiftPressed = false;
    } else if (e.key === 'Control' || e.key === 'Meta') {
      this._isCtrlPressed = false;
    }
  };

  render() {
    const { currentModel, isGenerating, showTokenUsage } = this.props;
    const capabilities = currentModel?.capabilities || [];
    const isImageUploadEnabled = capabilities.includes('vision') || capabilities.includes('tools');
    const wsConnected = wsService.connectionState === 'connected';
    const wsConnecting = wsService.connectionState === 'connecting';
    const supportTools = capabilities.includes('tools');

    if (!this.props.isVisible) {
      return null; 
    }
    
    const hideButtons = this._isVoiceRecording;
    
    return (
      <>
      <div className="input-container">
        <div className={'input-box' + (isGenerating ? ' loading' : '') + (this._isVoiceRecording ? ' voice-recording' : '')}>
            <div className={'image-gen-panel' + ((this._isImageGenMode && !hideButtons) ? ' active' : '')}>
              <div className="image-gen-panel-inner">
                {this._imageGenControlsMounted && this._renderImageGenControls()}
              </div>
            </div>
            <textarea 
              ref={this._textareaRef}
              autoFocus
              value={this.inputValue}
              className={"input-textarea" + (this._isDragOver ? " drag-over" : "") + (hideButtons ? " hidden" : "")}
              placeholder={this._isImageGenMode ? (this._isVideoGenMode ? "Describe the video you want to generate..." : "Describe the image you want to generate...") : "Type your message..."}  
              style={{ height: 66 }} 
              onChange={this._onPromptChange}
              onKeyDown={this._onPromptKeyDown}
              onInput={this._recalculateHeight}
              onPaste={isImageUploadEnabled ? this._onPaste : undefined}
              onFocus={() => setTimeout(this._recalculateHeight, 0)}
              onDragEnter={isImageUploadEnabled ? this._onDragEnter : undefined}
              onDragOver={isImageUploadEnabled ? this._onDragOver : undefined}
              onDragLeave={isImageUploadEnabled ? this._onDragLeave : undefined}
              onDrop={isImageUploadEnabled ? this._onDrop : undefined}
              onBlur={this._onTextareaBlur}
            />
            {this._slashQuery !== null && !this._isVoiceRecording && (
              <SkillMentionMenu
                matches={this._filteredSkills}
                hasAnySkills={this.props.skills.length > 0}
                highlightedIndex={this._slashHighlightIndex}
                onSelect={this._selectSkill}
                onHoverIndex={this._onSkillHighlightHover}
              />
            )}
            {this._keywordSuggestion && !this._isVoiceRecording && (
              <div className="keyword-suggestion-hint">
                <span className="keyword-suggestion-icon">✦</span>
                <span className="keyword-suggestion-body">
                  Add keywords: <em>{this._keywordSuggestion}</em>
                </span>
                <button className="keyword-suggestion-accept" onClick={this._acceptKeywordSuggestion} title="Accept (Tab)">
                  <ImgDocumentCheck size={12} /> Add
                </button>
                <button className="keyword-suggestion-dismiss" onClick={this._dismissKeywordSuggestion} title="Dismiss (Esc)">
                  <ImgCancel size={12} />
                </button>
                <span className="keyword-suggestion-keys"><kbd>Tab</kbd> · <kbd>Esc</kbd></span>
              </div>
            )}
            {this._imageRequiredHint && !this._isVoiceRecording && (
              <div className="image-required-hint">
                <span className="image-required-hint-icon">🖼</span>
                <span className="image-required-hint-body">
                  This suggestion transforms an image — add an image to work on
                </span>
                <button
                  className="image-required-hint-accept"
                  onClick={this._onAttachImageClick}
                  title="Add image"
                >
                  <ImgAttach size={12} /> Add image
                </button>
              </div>
            )}
            <div className={"input-controls" + (hideButtons ? " voice-recording" : "")}>
              {isImageUploadEnabled && <input type="file" ref={this._fileRef} accept="image/png,image/jpeg,image/gif,image/webp" multiple className="hidden" onChange={this._onImageUploadChange} />}
              {isImageUploadEnabled && !this._isImageGenMode && <input type="file" ref={this._docFileRef} accept=".pdf,.doc,.docx,.txt,.md,.csv,.js,.ts,.jsx,.tsx,.py,.java,.cpp,.c,.h,.cs,.go,.rb,.php,.html,.css,.xml,.svg,.json,.yaml,.yml,.sql,.log,.sh,.mp3,.webm,.ogg,.wav,.m4a,.flac,.aac" multiple className="hidden" onChange={this._onDocUploadChange} />}
              {isImageUploadEnabled && !this._isImageGenMode && <input type="file" ref={this._allTypesRef} accept="image/png,image/jpeg,image/gif,image/webp,.pdf,.doc,.docx,.txt,.md,.csv,.js,.ts,.jsx,.tsx,.py,.java,.cpp,.c,.h,.cs,.go,.rb,.php,.html,.css,.xml,.svg,.json,.yaml,.yml,.sql,.log,.sh,.mp3,.webm,.ogg,.wav,.m4a,.flac,.aac" multiple className="hidden" onChange={this._onAllTypesChange} />}
              {isImageUploadEnabled && <input type="file" ref={this._cameraRef} accept="image/*" capture="environment" className="hidden" onChange={this._onImageUploadChange} />}
              {isImageUploadEnabled && !hideButtons && <>
                <button
                  ref={this._attachBtnRef}
                  className="attach-btn"
                  onClick={this._isVideoGenMode ? this._onAttachImageClick : undefined}
                  title={this._isVideoGenMode ? 'Attach reference image (i2v)' : 'Attach File'}
                >
                  <ImgAttach size={16} />
                </button>
                {!this._isVideoGenMode && <ContextMenu showOnClick container={this._attachBtnRef}>
                  {() => (<>
                    {this._isMobile && <ContextMenuItem title={<><ImgCamera size={14} /> Camera</>} onClick={this._onCameraClick} />}
                    {this._isMobile && <ContextMenuItem title={<><ImgImage size={14} /> Images</>} onClick={this._onAttachImageClick} />}
                    {!this._isMobile && <ContextMenuItem title={<><ImgAttach size={14} /> From file</>} onClick={this._isImageGenMode ? this._onAttachImageClick : this._onAllTypesClick} />}
                    <ContextMenuItem title={<><ImgGallerySelect size={14} /> Gallery</>} onClick={() => { this._showGalleryPicker = true; }} />
                    {this._isMobile && !this._isImageGenMode && <ContextMenuItem title={<><ImgFileGeneric size={14} /> Files</>} onClick={this._onDocFilesClick} />}
                  </>)}
                </ContextMenu>}
              </>}
              {/* Tools dialog */}
              {!this._isImageGenMode && supportTools && !hideButtons && <button
                className={'attach-btn' + (this._isImageGenMode ? ' disabled' : '')}
                onClick={this.props.onOpenToolsConfig}
                title="Tools Configuration"
              >
                <ImgTools size={14} />
              </button>}
              {!hideButtons && <button 
                className={'attach-btn' + (this._isImageGenMode ? ' active' : '')} 
                onClick={this._onToggleImageGenMode} 
                title="Image Generation Mode"
              >
                <ImgNewImage />
              </button>}

              {this._isImageGenMode && !hideButtons && <span className="image-gen-summary">
                <button onClick={this._toggleImageGenControls} className="attach-btn btn-expand-image-options" title="Show Image Generation Options">
                  <span className="layout-name">{getLayoutIcon(this._imageLayout)}</span>
                  <span className="model-name">{this._isVideoGenMode ? `Video ${this._videoSize}p · ${this._videoFps}fps · ${this._videoLength}s` : this._getModelDisplayName(this._imageGenModel)}</span>
                  <span key={this._imageGenControlsExpanded ? 'collapse' : 'expand'} className="expand-icon-anim">{this._imageGenControlsExpanded ? <ImgCollapse /> : <ImgExpand />}</span>
                </button>
              </span>}
              {this._isImageGenMode && !!this.inputValue && !hideButtons &&<button 
                disabled={this._generatePromptCompletion.isPending || isGenerating}
                className={`attach-btn btn-magic-prompt ${this._generatePromptCompletion.isPending ? 'active' : ''}`} 
                title="Generate Prompt Magic"
                onClick={this._generateMagicPrompt}
              >
                <ImgMagic />
              </button>}
              {this._isImageGenMode && this._currentEstimate && !this._currentEstimate.canRun && !hideButtons && (
                <span
                  ref={this._memoryBadgeRef}
                  className={`image-model-estimate warning${(window.user?.role === 'admin' && this._imageEstimates?.memoryDistribution) ? ' has-popup' : ''}`}
                  title={
                    window.user?.role === 'admin' && this._imageEstimates?.memoryDistribution
                      ? 'Click to see memory distribution'
                      : `Warning: ${this._currentEstimate.modelName} requires ${this._currentEstimate.sizeGb} GB VRAM — may need to unload other models. Available: ${this._currentEstimate.availableVramGb?.toFixed(1) ?? '?'} GB`
                  }
                  onClick={this._onMemoryBadgeClick}
                >
                  <ImgWarning size={12} /> {this._currentEstimate.sizeGb} GB
                </span>
              )}
              {(this._imageMetas.length > 0 || this._pendingUploads.some(p => p.isImage)) && !hideButtons && <div className="attached-images">
                {this._imageMetas.map((meta, index) => (
                  <div key={meta.id} className="attached-image" title={meta.filename || 'Image'}>
                    <ImageWrapper
                      src={`/image/${meta.id}`}
                      className="attached-image-preview"
                      alt={meta.filename || 'Attached'}
                    />
                    <button className="remove-btn" onClick={(e) => { e.stopPropagation(); this._onRemoveImage(index); }} title="Remove">&#x2715;</button>
                  </div>
                ))}
                {this._pendingUploads.filter(p => p.isImage).map(p => (
                  <span key={p.id} className="attached-image attached-image-uploading" title={`Uploading ${p.filename}… ${Math.round(p.progress * 100)}% (click to cancel)`} onClick={() => void this._onCancelUploadClick(p.id)}>
                    <UploadProgressRing progress={p.progress} size={30} />
                    <button className="remove-btn" title="Stop uploading" onClick={(e) => { e.stopPropagation(); void this._onCancelUploadClick(p.id); }}>&#x2715;</button>
                  </span>
                ))}
              </div>}

              {(this._attachmentMetas.length > 0 || this._pendingUploads.some(p => !p.isImage)) && !hideButtons && <div className="attached-files">
                {this._attachmentMetas.map((meta, index) => {
                  const isTextFragment = meta.filename.startsWith('text-fragment-') && meta.filename.endsWith('.txt');

                  if (isTextFragment) {
                    const text = meta.textContent ? meta.textContent.trim().substring(0, 80) + (meta.textContent.length > 80 ? '...' : '') : '';
                    return (
                      <div key={meta.id} className="attached-text-fragment">
                        <span className="text-fragment-preview">
                          {text || 'Pasted text'}
                        </span>
                        {meta.textContent && <span className="text-fragment-size">{this._formatSize(meta.size)}</span>}
                        <button className="remove-btn" onClick={() => this._onRemoveAttachment(index)} title="Remove"><ImgCancel size={14} /></button>
                      </div>
                    );
                  }

                  const attachmentType = meta.type;
                  const isViewable = attachmentType === 'text' || attachmentType === 'pdf';
                  const isAudio = attachmentType === 'audio';
                  const iconType = getAttachmentIconComponent(meta.contentType, meta.filename);
                  
                  // Get appropriate icon component
                  let IconComponent: React.ComponentType<{ size?: number }>;
                  switch (iconType) {
                    case 'pdf': IconComponent = ImgFilePdf; break;
                    case 'doc': IconComponent = ImgFileDoc; break;
                    case 'docx': IconComponent = ImgFileDocx; break;
                    case 'txt': IconComponent = ImgFileTxt; break;
                    case 'csv': IconComponent = ImgFileCsv; break;
                    case 'html': IconComponent = ImgFileHtml; break;
                    case 'svg': IconComponent = ImgFileSvg; break;
                    case 'sql': IconComponent = ImgFileSql; break;
                    case 'log': IconComponent = ImgFileLog; break;
                    default: IconComponent = ImgFileGeneric; break;
                  }
                  
                  if (isAudio) {
                    return (
                      <div key={meta.id} className="attached-file attached-file-audio">
                        <span className="attached-file-icon"><ImgMusic size={18} /></span>
                        <span className="attached-file-name" title={meta.filename}>{meta.filename}</span>
                        <audio src={`/image/${meta.id}`} controls style={{ height: 28, maxWidth: 200 }} />
                        <button className="remove-btn" onClick={() => this._onRemoveAttachment(index)} title="Remove"><ImgCancel size={14} /></button>
                      </div>
                    );
                  }
                  
                  return (
                    <div key={meta.id} className="attached-file">
                      <span className="attached-file-icon"><IconComponent size={18} /></span>
                      <span 
                        className={"attached-file-name" + (isViewable ? " viewable" : "")}
                        onClick={isViewable ? () => this._onViewAttachment(meta) : undefined}
                        title={isViewable ? "Click to view" : meta.filename}
                      >
                        {meta.filename}
                      </span>
                      <button className="remove-btn" onClick={() => this._onRemoveAttachment(index)} title="Remove"><ImgCancel size={14} /></button>
                    </div>
                  );
                })}
                {this._pendingUploads.filter(p => !p.isImage).map(p => (
                  <span key={p.id} className="attached-file attached-file-uploading" title={`Uploading ${p.filename}… ${Math.round(p.progress * 100)}% (click to cancel)`} onClick={() => void this._onCancelUploadClick(p.id)}>
                    <UploadProgressRing progress={p.progress} size={16} />
                    <span className="attached-file-name">{p.filename}</span>
                    <button className="remove-btn" title="Stop uploading" onClick={(e) => { e.stopPropagation(); void this._onCancelUploadClick(p.id); }}><ImgCancel size={14} /></button>
                  </span>
                ))}
              </div>}

              <div className={"input-actions" + (hideButtons ? " voice-recording" : "")}>

                {!this._isImageGenMode && this._hasKeyboard && !hideButtons && <div className="key-help">
                  <div className="key-help-bindings">
                    <span className={this._isShiftPressed ? 'help-enabled' : ''}>Shift <ImgKeyArrow /> =</span>
                    <span title="Start chat with this message with keeping original system message" className={this._isCtrlPressed ? 'help-enabled' : ''}>Ctrl <ImgKeyArrow /> =</span>
                  </div>
                  <div className="key-help-info">
                    <span className={this._isShiftPressed ? 'help-enabled' : ''}>New line</span>
                    <span title="Start chat with this message with keeping original system message" className={this._isCtrlPressed ? 'help-enabled' : ''}>{this.props.isAlternativeMode ? "Send" : "Start over"}</span>
                  </div>
                </div>}

                {showTokenUsage && !hideButtons && !this._isImageGenMode && <TokensCounter
                  isGenerating={isGenerating}
                  className={!this._imageGenControlsExpanded ? "counter-image-gen-combined" : ""}
                  inputValue={this.inputValue}
                  hasImage={this._imageMetas.length > 0}
                  currentModel={this.props.currentModel}
                  tools={this.props.tools}
                  conversation={this.props.conversation}
                  attachmentMetas={[...this._imageMetas, ...this._attachmentMetas]}
                  predictLength={this.props.predictLength}
                  onTokensChange={this._onTokensChange}
                />}

                <VoiceRecorder 
                  onStartRecording={this._onStartRecording} 
                  onTranscription={this._onTranscriptionDone}
                  onRecordingChange={this._onRecordingChange}
                />

                {isGenerating && !hideButtons && (
                  <>
                    <button
                      className="round-btn round-btn-stop"
                      onClick={this._onCancelRequestClick}
                      title="Stop"
                      type="button"
                    >
                      <ImgStop size={16} />
                    </button>
                    <button
                      className="round-btn round-btn-steer"
                      onClick={() => this._sendMessage()}
                      title="Send (steer)"
                      type="button"
                      disabled={!this.inputValue.trim() || !wsConnected}
                    >
                      <ImgReturn size={16} />
                    </button>
                  </>
                )}
                {this._uploading.isPending && !hideButtons && <span className="uploading-indicator" title="Uploading attachments…"><Loader size="xsm" /></span>}
                {!isGenerating && !hideButtons && <ToggleModeButton
                  className={'btn btn-primary' + (this._uploading.isPending ? ' uploading' : '')}
                  title={wsConnecting ? 'Connecting...' : this._isImageGenMode ? "Generate" : "Send"}
                  isAlternativeMode={this.props.isAlternativeMode}
                  onModeSwitch={this.props.onModeSwitch}
                  alternativeTitle="Start"
                  style={{ display: 'block', minWidth: 55 }}
                  onClick={() => this._sendMessage()}
                  onClickAlternative={() => this._sendMessage(true)}
                  disabled={this._isSendDisabled}
                  visible={true}
                />}
              </div>
            </div>
        </div>
      </div>

      {this._showGalleryPicker && ReactDOM.createPortal(
        <React.Suspense fallback={null}>
          <ImageGalleryPicker
            pickerMode={{ maxSelect: 3, onConfirm: this._onGalleryPickerConfirm }}
            onClose={() => { this._showGalleryPicker = false; }}
            onNavigateToChat={() => {}}
          />
        </React.Suspense>,
        document.body
      )}

      {this._showMemoryPopup && this._imageEstimates?.memoryDistribution && (
        <MemoryDistributionPopup
          distribution={this._imageEstimates.memoryDistribution}
          required={this._currentEstimate ? {
            label: `${this._currentEstimate.modelName} (selected)`,
            sizeGb: this._currentEstimate.sizeGb,
          } : null}
          anchor={this._memoryBadgeRef}
          onClose={this._closeMemoryPopup}
        />
      )}
      </>
    );
  }

  private _imageEstimatesFetchedAt = 0;

  /** Refresh estimates if stale (>30s). */
  private _ensureFreshEstimates = () => {
    if (!this._imageEstimates || Date.now() - this._imageEstimatesFetchedAt > 30_000) {
      void this._fetchImageEstimates();
    }
  };

  private _onMemoryBadgeClick = () => {
    if (window.user?.role !== 'admin' || !this._imageEstimates?.memoryDistribution) return;
    this._ensureFreshEstimates();
    this._showMemoryPopup = !this._showMemoryPopup;
  };

  @action
  private _closeMemoryPopup = () => {
    this._showMemoryPopup = false;
  };

  /** Add a reference image from an existing attachment ID (e.g. "Use as reference" from chat message) */
  @action.bound
  public async addImageFromAttachmentId(attachmentId: string): Promise<void> {
    // Dedupe: pressing "Use config" again (or re-using the same reference) must not
    // add the same attachment twice or increment its refcount a second time.
    if (this._imageMetas.some(m => m.id === attachmentId)) {
      return;
    }
    // Auto-switch to last used i2i model if needed
    if (!this._imageAttachEnabled && !this._isVideoGenMode) {
      const last = localStorage.getItem('lastImageEditModel') as ImageGenModel | null;
      this._setImageGenModel(last || 'Flux2.Klein');
    }
    if (this._maxImages > 0 && this._imageMetas.length >= this._maxImages) {
      this._showMaxImagesError();
      return;
    }
    // Reuse an existing attachment: increment its reference counter and track it by id.
    // Tracked in-flight so a fast "Generate" can't run before the ref is secured.
    const pendingId = `p-${++this._uploadSeq}`;
    const controller = new AbortController();
    this._uploadAborts.set(pendingId, controller);
    this._pendingUploads.push({ id: pendingId, filename: 'Image…', isImage: true, progress: 0 });
    try {
      const meta = await imageService.addAttachmentRef(attachmentId, this._uploading, controller.signal);
      this._removePendingUpload(pendingId);
      this._imageMetas.push(meta);
      this._imageRequiredHint = false;
      // Auto-select 'as-image' layout + size when the first image is added (non-video)
      if (this._imageMetas.length === 1 && !this._isVideoGenMode) {
        this._setImageLayout('as-image');
        this._setImageSize('as-image');
      }
    } catch (error) {
      this._removePendingUpload(pendingId);
      if (!(error instanceof DOMException && error.name === 'AbortError')) {
        toastService.error('Failed to add image', error instanceof Error ? error.message : String(error));
      }
    } finally {
      this._uploadAborts.delete(pendingId);
    }
  }

  /** Called when gallery picker confirms selection */
  @action.bound
  private async _onGalleryPickerConfirm(ids: string[]): Promise<void> {
    this._showGalleryPicker = false;
    for (const id of ids) {
      await this.addImageFromAttachmentId(id);
    }
  }

  private _renderImageGenControls() {
    // for video gen mode we show a simplified UI and hide the style picker since styles are not supported by video gen models.
    // In video mode the container itself collapses (no style picker row to anchor below), so its
    // padding/border animate via the --video --collapsed modifiers instead of unmounting instantly.
    return (
      <div className={"image-gen-controls image-gen-controls-models" + (this._isVideoGenMode ? ' image-gen-controls--video' + (this._imageGenControlsExpanded ? '' : ' image-gen-controls--collapsed') : '')}>
        <div className={"image-gen-expandable" + (this._imageGenControlsExpanded ? " expanded" : "")}>
          <div className="image-gen-expandable-inner">

          {/* Line 1: layout proportions + image/video mode toggle */}
          <div className="control-line">
            <ImageLayoutPicker
              layout={this._imageLayout}
              onSelect={this._setImageLayout}
              size={this._imageSize}
              onSizeSelect={this._setImageSize}
              imageAttached={this._imageMetas.length > 0}
              asImageSize={this._firstImageDimensions ?? undefined}
              canUseHighRes={this._canUseHighRes}
              video={this._isVideoGenMode ? {
                size: String(this._videoSize) as import('./ImageLayoutPicker').VideoSizeChoice,
                onSizeSelect: (v) => this._setVideoSize(parseInt(v)),
              } : undefined}
            />
            <div className="gen-mode-toggle">
              <button
                className={'gen-mode-btn' + (!this._isVideoGenMode ? ' active' : '')}
                onClick={this._setImageMode}
                title="Image Generation"
              >
                <ImgImageMode />
                <span>Image</span>
              </button>
              <button
                className={'gen-mode-btn' + (this._isVideoGenMode ? ' active' : '')}
                onClick={this._setVideoMode}
                title="Video Generation"
              >
                <ImgVideoMode />
                <span>Video</span>
              </button>
            </div>
          </div>

          {/* Line 2: model picker */}
          <div className="control-line">
            {!this._isVideoGenMode
              ? <ImageModelPicker model={this._imageGenModel} onSelect={this._setImageGenModel} hasImages={this._imageMetas.length > 0} />
              : <VideoModelPicker model={this._videoGenModel} onSelect={this._setVideoGenModel} />
            }
          </div>

          {/* Line 3: video parameters (video mode only) */}
          {this._isVideoGenMode && (
            <div className="control-line video-params-line">
              <div className="video-param-group">
                <label>FPS</label>
                <div className="video-param-options">
                  {([8, 12, 16, 24] as const).map(f => (
                    <button key={f} className={'video-param-btn' + (this._videoFps === f ? ' active' : '')} onClick={() => this._setVideoFps(f)}>{f}</button>
                  ))}
                </div>
              </div>
              <div className="video-param-group">
                <label>Length</label>
                <div className="video-param-options">
                  {([3, 5, 7, 10, 12, 15] as const).map(l => (
                    <button key={l} className={'video-param-btn' + (this._videoLength === l ? ' active' : '')} title={l === 3 ? '3 seconds (MiniMax H3 rounds up to 5s)' : l === 12 ? '12 seconds (MiniMax H3 rounds up to 15s)' : l + ' seconds'} onClick={() => this._setVideoLength(l)}>{l}s</button>
                  ))}
                </div>
              </div>
            </div>
          )}

          </div>
        </div>

          {/* Line 4: style picker — image mode only */}
        {!this._isVideoGenMode && <ImageStylePicker
          model={this._imageGenModel}
          selectedStyles={this._selectedStyles}
          onChange={this._setSelectedStyles}
          deferLoad={!this._isImageGenMode}
        />}
      </div>
    );
  }

  private _recalculateHeight = () => {
      this._textareaRef.current!.style.height = 'auto';
      const newHeight = Math.min(192, Math.max(48, this._textareaRef.current!.scrollHeight));
      this._textareaRef.current!.style.height = newHeight + 'px';
  };

  @action
  _onPromptKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (this._slashQuery !== null) {
      const matches = this._filteredSkills;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        this._slashHighlightIndex = matches.length ? (this._slashHighlightIndex + 1) % matches.length : 0;
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        this._slashHighlightIndex = matches.length ? (this._slashHighlightIndex - 1 + matches.length) % matches.length : 0;
        return;
      }
      if ((e.key === 'Enter' || e.key === 'Tab') && matches.length > 0) {
        e.preventDefault();
        this._selectSkill(matches[this._slashHighlightIndex]);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        this._slashQuery = null;
        return;
      }
    }

    if (e.key === 'Tab' && this._keywordSuggestion !== null) {
      e.preventDefault();
      this._acceptKeywordSuggestion();
      return;
    }
    if (e.key === 'Escape' && this._keywordSuggestion !== null) {
      this._dismissKeywordSuggestion();
      return;
    }
    if (e.key === 'Escape' && this._imageGenControlsExpanded && this._isImageGenMode) {
      this._toggleImageGenControls();
      return;
    }
    
    if (e.key === 'Enter') {
      if (!e.shiftKey) {
        const isChangeMode = e.altKey || e.ctrlKey || e.metaKey;
        e.preventDefault();
        const startFromBeginning = !this.props.isAlternativeMode && isChangeMode || this.props.isAlternativeMode && !isChangeMode;
        this._sendMessage(startFromBeginning);
        return;
      }

      if (!e.shiftKey) {
        const textarea = this._textareaRef.current!;
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        
        // Insert newline at cursor
        const before = this.inputValue.substring(0, start);
        const after = this.inputValue.substring(end);
        this.inputValue = before + "\n" + after;

        this._textareaRef.current!.value = this.inputValue;
        textarea.selectionStart = textarea.selectionEnd = start + 1;
      }
      setTimeout(this._recalculateHeight, 0);
    }
  };

  @action
  _onPaste = async (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    if (this._isImageGenMode && !this._imageAttachEnabled) {
      // Standard image gen mode: no attachments, let browser handle plain text naturally
      return;
    }

    const clipboardData = e.clipboardData;
    if (!clipboardData) return;

    const text = clipboardData.getData('text/plain');
    // Prevent default immediately to avoid text being pasted before we process it
    e.preventDefault();

    // Shift = paste as plain text (never an attachment). Prefer the paste event's own
    // shiftKey (accurate in Chromium/WebKit, no stuck-state risk); fall back to the
    // global flag only for Firefox, whose ClipboardEvent does not expose shiftKey.
    const eventShiftKey = (e.nativeEvent as unknown as KeyboardEvent).shiftKey;
    const forcePlain = eventShiftKey !== undefined ? eventShiftKey : this._isShiftPressed;
    const result = await imageService.getAttachmentsFromDataTransfer(clipboardData, forcePlain);

    if (result.files.length) {
      for (const file of result.files) {
        if (imageService.isImageFile(file)) {
          await this._processImageFile(file);
        } else if (!(this._isImageGenMode && this._imageAttachEnabled)) {
          // Non-image files only blocked in image-attach (video/image-edit) modes
          const meta = await this._uploadFile(file);
          if (meta) this._attachmentMetas.push(meta);
        }
      }
      return;
    } 
    
    if (result.textFragment) {
      if (!(this._isImageGenMode && this._imageAttachEnabled)) {
        // Text fragment attachment blocked only in image-attach modes
        const textFile = imageService.createTextFragmentFile(result.textFragment);
        const meta = await this._uploadFile(textFile);
        if (meta) {
          this._attachmentMetas.push(meta);
          return;
        }
        // Upload failed — fall through and paste the text as plain text
      }
      // In image-attach mode fall through and paste as plain text
    }
    
    // Regular text paste - use execCommand for proper undo history and cursor placement
    if (text) {
      const textarea = this._textareaRef.current!;
      const inserted = document.execCommand('insertText', false, text);
      if (inserted) {
        this.inputValue = textarea.value;
        this._recalculateHeight();
      } else {
        // Fallback for environments where execCommand is unavailable
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const before = this.inputValue.substring(0, start);
        const after = this.inputValue.substring(end);
        this.inputValue = before + text + after;
        setTimeout(() => {
          textarea.selectionStart = textarea.selectionEnd = start + text.length;
          this._recalculateHeight();
        }, 0);
      }
      this.save();
    }
  };

  @action
  private _onDragEnter = (e: React.DragEvent<HTMLTextAreaElement>) => {
    e.preventDefault();
    e.stopPropagation();
    this._isDragOver = true;
  };

  @action
  private _onDragOver = (e: React.DragEvent<HTMLTextAreaElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  @action
  private _onDragLeave = (e: React.DragEvent<HTMLTextAreaElement>) => {
    e.preventDefault();
    e.stopPropagation();
    this._isDragOver = false;
  };

  @action
  private _onDrop = async (e: React.DragEvent<HTMLTextAreaElement>) => {
    e.preventDefault();
    e.stopPropagation();
    this._isDragOver = false;

    const files = e.dataTransfer.files;
    if (!files || files.length === 0) return;

    for (const file of Array.from(files)) {
      if (!imageService.isFileSupported(file) || !imageService.validateAttachmentFile(file)) {
        continue;
      }

      if (imageService.isImageFile(file)) {
        await this._processImageFile(file);
      } else if (!(this._isImageGenMode && this._imageAttachEnabled)) {
        // Non-image files only blocked in image-attach (video/image-edit) modes
        const meta = await this._uploadFile(file);
        if (meta) this._attachmentMetas.push(meta);
      }
    }
  };

  @action
  _onImageUploadChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) {
      this._fileRef.current!.value = '';
      return;
    }

    try {
      for (const file of Array.from(files)) {
        if (!imageService.isFileSupported(file) || !imageService.validateAttachmentFile(file)) {
          continue;
        }

        if (imageService.isImageFile(file)) {
          await this._processImageFile(file);
        }
      }
    } catch (error) {
      console.error('Error processing uploaded file:', error);
      alert('Failed to process the uploaded file.');
    }
    this._fileRef.current!.value = '';
  }

  /**
   * Upload a single file and track it in flight: shows a placeholder row while the
   * request is running (loader = `_uploading`) and surfaces failures as a toast.
   * Returns the created attachment meta, or null when the upload failed.
   */
  @action
  private async _uploadFile(file: File): Promise<AttachmentMeta | null> {
    const pendingId = `p-${++this._uploadSeq}`;
    const controller = new AbortController();
    this._uploadAborts.set(pendingId, controller);
    this._pendingUploads.push({ id: pendingId, filename: file.name, isImage: imageService.isImageFile(file), progress: 0 });
    try {
      const meta = await imageService.uploadAttachment(file, this._uploading, controller.signal, fraction => {
        runInAction(() => {
          const pending = this._pendingUploads.find(p => p.id === pendingId);
          if (pending) pending.progress = fraction;
        });
      });
      this._removePendingUpload(pendingId);
      return meta;
    } catch (error) {
      this._removePendingUpload(pendingId);
      if (error instanceof DOMException && error.name === 'AbortError') {
        return null; // user cancelled — already confirmed by them
      }
      toastService.error('Upload failed', `${file.name}: ${error instanceof Error ? error.message : String(error)}`);
      return null;
    } finally {
      this._uploadAborts.delete(pendingId);
    }
  }

  @action
  private _removePendingUpload(pendingId: string) {
    this._pendingUploads = this._pendingUploads.filter(p => p.id !== pendingId);
  }

  /** Click on an in-flight placeholder: confirm, then abort the upload in flight. */
  @action
  private async _onCancelUploadClick(pendingId: string) {
    if (!await modalService.showConfirmation('Stop uploading this attachment?', 'Cancel Upload')) return;
    this._uploadAborts.get(pendingId)?.abort();
    this._removePendingUpload(pendingId);
    this._uploadAborts.delete(pendingId);
  }

  @action
  private _processImageFile = async (file: File): Promise<void> => {
    if (!imageService.isImageFile(file)) {
      return;
    }
    // Auto-switch to last used i2i model when attaching an image in non-i2i mode
    if (!this._imageAttachEnabled && !this._isVideoGenMode) {
      const last = localStorage.getItem('lastImageEditModel') as ImageGenModel | null;
      this._setImageGenModel(last || 'Flux2.Klein');
    }
    if (this._imageMetas.length >= this._maxImages && this._maxImages > 0) {
      this._showMaxImagesError();
      return;
    }
    // Upload immediately — the composer tracks the attachment by id from now on.
    // `_uploadFile` keeps the send button locked until the id is ready.
    const meta = await this._uploadFile(file);
    if (meta) {
      this._imageMetas.push(meta);
      this._unboundImageIds.add(meta.id);
      // Auto-select 'as-image' layout + size when the first image is attached (non-video)
      if (this._imageMetas.length === 1 && !this._isVideoGenMode) {
        this._setImageLayout('as-image');
        this._setImageSize('as-image');
      }
    }
  };

  /** After a generation that keeps its reference images in the composer, secure a server
   *  ref for each image whose composer ref was just consumed (transferred to the generated
   *  user message) — i.e. every still-unbound fresh upload. Re-added / previously generated
   *  images already hold a separate (or message-backed) ref and need no compensation.
   *  Fire-and-forget: must not gate sending; the `$inc refCount` is independent of the
   *  generation's `$set messageId`, so it is race-safe in either order. */
  private _keepImageRefs(imageIds: string[]) {
    for (const id of imageIds) {
      if (!this._unboundImageIds.has(id)) continue;
      this._unboundImageIds.delete(id);
      imageService.addAttachmentRef(id).catch(error => {
        toastService.error('Failed to keep reference image', error instanceof Error ? error.message : String(error));
      });
    }
  }

  private _sendMessageInternal = async (content: string, startFromBeginning: boolean = false) => {
    // Dismiss any pending keyword suggestion
    this._keywordSuggestion = null;
    if (this._keywordSuggestionTimer !== null) {
      clearTimeout(this._keywordSuggestionTimer);
      this._keywordSuggestionTimer = null;
    }

    // If in image generation mode, append generation parameters to message
    let messageToSend = content;
    if (this._isImageGenMode) {
      // Video generation mode
      if (this._isVideoGenMode) {
        const imageIds = this._imageMetas.map(m => m.id);
        // Video models don't support 'wide'/'tall' — fall back to landscape/portrait.
        // For 'as-image' (layout or size) in video mode: derive orientation from image aspect ratio
        let videoLayout: ImageLayout = this._imageLayout === 'wide' ? 'landscape' : this._imageLayout === 'tall' ? 'portrait' : this._imageLayout;
        if (videoLayout === 'as-image' || this._imageSize === 'as-image') {
          const dims = this._firstImageDimensions;
          if (dims) {
            const ratio = dims.width / dims.height;
            videoLayout = ratio > 1.2 ? 'landscape' : ratio < 0.8 ? 'portrait' : 'square';
          } else {
            videoLayout = 'square';
          }
        }
        // Keep the prompt and the reference image in the composer so the user can edit
        // and re-generate; only non-image attachments are dropped (not part of generation).
        this._keepImageRefs(imageIds);
        this._attachmentMetas = [];
        this.props.onVideoGenerate(imageIds, messageToSend, this._videoGenModel, videoLayout, this._videoSize, this._videoFps, this._videoLength);
        setTimeout(this._recalculateHeight, 0);
        return;
      }

      // Image generation mode
      const styles = this._selectedStyles;

      for (const style of styles) {
        const styleParsed = parseLoraStyleName(style.name);
        // search if any of the keywords appear in the prompt
        if (styleParsed.keywords) {
          let keywordMatched = false;
          for (const keyword of styleParsed.keywords) {
            if (messageToSend.toLowerCase().includes(keyword.toLowerCase())) {
              keywordMatched = true;
              break;
            }
          }
          if (!keywordMatched) {
            // add first keyword to the prompt
            messageToSend += `. ${styleParsed.keywords[0]}`;
          }
        }
      }
      
      // Keep the prompt and the reference images in the composer so the user can edit
      // and re-generate; only non-image attachments are dropped (not part of generation).
      const keptImageIds = this._imageMetas.map(m => m.id);
      this.props.onImageGenerate(
        keptImageIds,
        messageToSend,
        '',
        this._imageGenModel,
        this._imageLayout,
        styles,
        this._outputSize?.width,
        this._outputSize?.height,
        this._imageGenModel === 'qwen2.1t',
      );
      this._keepImageRefs(keptImageIds);
      this._attachmentMetas = [];
      this._imageRequiredHint = false;
      return;
    }
    
    this.inputValue = '';
    this._slashQuery = null;
    const allIds: string[] = [];
    allIds.push(...this._imageMetas.map(m => m.id));
    allIds.push(...this._attachmentMetas.map(m => m.id));
    this.props.onSendMessage(messageToSend, allIds, startFromBeginning);
    if (this._fileRef.current) {
      this._fileRef.current.value = '';
    }
    for (const m of this._imageMetas) this._unboundImageIds.delete(m.id);
    this._imageMetas = [];
    this._attachmentMetas = [];
    this._imageRequiredHint = false;
    setTimeout(this._recalculateHeight, 0);
  }

  @action
  private _sendMessage = async (startFromBeginning: boolean = false) => {
    const value = this.inputValue.trim();

    // While the model is generating, "Send" becomes a steering action: queue a text-only
    // upcoming message that the server loop picks up at the end of the current round.
    if (this.props.isGenerating && this.props.onSteerMessage) {
      if (!value) {
        this._textareaRef.current?.focus();
        return;
      }
      this.props.onSteerMessage(value);
      this.inputValue = '';
      this._slashQuery = null;
      setTimeout(this._recalculateHeight, 0);
      return;
    }

    // An attachment is still uploading — a message may only carry finished
    // attachment ids, so block sending (the button is disabled too, but the
    // Enter key reaches this method directly).
    if (this._uploading.isPending) {
      return;
    }

    const canSend = !!value || (this._imageMetas.length > 0 || this._attachmentMetas.length > 0) && this.props.hasSystemMessage;

    if (!canSend){
      this._textareaRef.current?.focus();
      return;
    }

    this._sendMessageInternal(value, startFromBeginning);
  }

  @action
  private _onPromptChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    this.inputValue = e.target.value;
    this.save();
    if (!this._isImageGenMode){
      this._prefetchModel?.();
    }
    this._updateSlashMenu(this.inputValue, e.target.selectionStart ?? this.inputValue.length);
    // Reset suggestion on every keystroke, then reschedule after 2 s of silence
    this._keywordSuggestion = null;
    if (this._keywordSuggestionTimer !== null) {
      clearTimeout(this._keywordSuggestionTimer);
      this._keywordSuggestionTimer = null;
    }
    if (this._isImageGenMode && !this._isVideoGenMode && this._selectedStyles.length > 0 && this.inputValue.trim()) {
      this._keywordSuggestionTimer = setTimeout(action(() => {
        this._keywordSuggestion = this._computeKeywordSuggestion();
        this._keywordSuggestionTimer = null;
      }), 2000);
    }
  };

  /** Detects a "/skillname" token ending at the caret and opens/updates/closes the slash menu accordingly. */
  @action
  private _updateSlashMenu(fullValue: string, caret: number) {
    if (this._isImageGenMode) {
      this._slashQuery = null;
      return;
    }
    const uptoCaret = fullValue.slice(0, caret);
    const match = /(?:^|\s)\/([^\s/]*)$/.exec(uptoCaret);
    if (match) {
      const isNewQuery = this._slashQuery === null;
      this._slashQuery = match[1];
      this._slashStartPos = caret - match[1].length - 1;
      if (isNewQuery) this._slashHighlightIndex = 0;
      // Lazy-load skills on first "/" — the list is otherwise only fetched
      // here, so we avoid a first-load request until the menu is actually used.
      void skillService.loadSkills();
    } else {
      this._slashQuery = null;
    }
  }

  /** Skills matching the current slash query, ranked by name-prefix, then name-substring, then description match. */
  private get _filteredSkills(): SkillDto[] {
    const query = (this._slashQuery ?? '').trim().toLowerCase();
    const skills = this.props.skills;
    if (!query) return skills;
    const startsWith: SkillDto[] = [];
    const nameIncludes: SkillDto[] = [];
    const descIncludes: SkillDto[] = [];
    for (const skill of skills) {
      const name = skill.name.toLowerCase();
      if (name.startsWith(query)) startsWith.push(skill);
      else if (name.includes(query)) nameIncludes.push(skill);
      else if (skill.description.toLowerCase().includes(query)) descIncludes.push(skill);
    }
    return [...startsWith, ...nameIncludes, ...descIncludes];
  }

  @action.bound
  private _onSkillHighlightHover(index: number) {
    this._slashHighlightIndex = index;
  }

  /** Applies the selected skill: strips the "/query" token from the input and notifies the parent. */
  @action.bound
  private _selectSkill(skill: SkillDto) {
    if (this._slashQuery === null) return;
    const before = this.inputValue.slice(0, this._slashStartPos);
    const after = this.inputValue.slice(this._slashStartPos + 1 + this._slashQuery.length);
    this.inputValue = before + after;
    this._slashQuery = null;
    this.save();
    this.props.onApplySkill(skill);
    setTimeout(() => {
      this._textareaRef.current?.focus();
      this._recalculateHeight();
    }, 0);
  }

  @action.bound
  private _onTextareaBlur() {
    // Deferred so a menu item's onMouseDown (which preventDefaults the blur) still wins the race.
    setTimeout(action(() => { this._slashQuery = null; }), 150);
  }

  public save() {
    localStorage.setItem('promptInput_' + (this.props.chatId || 'new'), this.inputValue);
  }

  private _doPrefetchModel = async () => {
    const currentModel = this.props.currentModel;
    let expectedMinContext = this.props.tokensInConversation + (this._messageTokens || 0);
    if (currentModel?.name === 'gpt-oss:20b') {
      // gpt-oss:20b requires special handling due to its large context size
      expectedMinContext = Math.max(expectedMinContext, 61440);
    }

    const activeModel = modelService.activeModels.find(activeModel =>
      activeModel.server === currentModel?.server && activeModel.name === resolveLoadedModelName(currentModel)
    );

    if (activeModel || !currentModel) return;

    await modelService.prefetchModel(currentModel, expectedMinContext);
  };

  @action.bound
  private _onTokensChange(tokens: number | null) {
    this._messageTokens = tokens;
  }

  @action
  private _onCancelRequestClick = () => {
    this.props.onCancelRequest();
  }

  @action
  private _onAttachImageClick = () => {
    if (this._fileRef.current) {
      this._fileRef.current.click();
    }
  }

  @action
  private _onCameraClick = () => {
    this._cameraRef.current?.click();
  }

  @action
  private _onAllTypesClick = () => {
    this._allTypesRef.current?.click();
  }

  @action
  private _onDocFilesClick = () => {
    this._docFileRef.current?.click();
  }

  @action
  _onAllTypesChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) {
      this._allTypesRef.current!.value = '';
      return;
    }
    try {
      for (const file of Array.from(files)) {
        if (!imageService.isFileSupported(file) || !imageService.validateAttachmentFile(file)) continue;
        if (imageService.isImageFile(file)) {
          await this._processImageFile(file);
        } else {
          const meta = await this._uploadFile(file);
          if (meta) this._attachmentMetas.push(meta);
        }
      }
    } catch (error) {
      console.error('Error processing uploaded file:', error);
      alert('Failed to process the uploaded file.');
    }
    this._allTypesRef.current!.value = '';
  }

  @action
  _onDocUploadChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) {
      this._docFileRef.current!.value = '';
      return;
    }

    try {
      for (const file of Array.from(files)) {
        if (!imageService.isFileSupported(file) || !imageService.validateAttachmentFile(file)) {
          continue;
        }
        const meta = await this._uploadFile(file);
        if (meta) this._attachmentMetas.push(meta);
      }
    } catch (error) {
      console.error('Error processing uploaded file:', error);
      alert('Failed to process the uploaded file.');
    }
    this._docFileRef.current!.value = '';
  }

  @action
  private _onRemoveImage = async (index: number) => {
    if (!await modalService.showConfirmation('Remove this image?', 'Remove Image')) return;
    const [removed] = this._imageMetas.splice(index, 1);
    if (this._imageMetas.length === 0) {
      if (this._fileRef.current) this._fileRef.current.value = '';
      // Reset 'as-image' choices to defaults when all images removed
      if (this._imageLayout === 'as-image') this._setImageLayout('square');
      if (this._imageSize === 'as-image') this._setImageSize('1024');
    }
    if (removed) {
      this._unboundImageIds.delete(removed.id);
      void imageService.unrefAttachments([removed.id]);
    }
  }

  @action
  private _onRemoveAttachment = (index: number) => {
    const [removed] = this._attachmentMetas.splice(index, 1);
    if (removed) void imageService.unrefAttachments([removed.id]);
  }

  @action.bound
  private async _onViewAttachment(meta: AttachmentMeta) {
    try {
      const attachmentType = meta.type;

      if (attachmentType === 'pdf') {
        await modalService.show(PdfViewerDialog, {
          attachmentId: meta.id,
          filename: meta.filename
        });
        return;
      }

      // Fetch the full text content from the server by attachment id.
      const response = await fetch(`/image/${meta.id}`);
      const blob = await response.blob();
      const text = await blob.text();

      await modalService.show(TextViewerDialog, {
        attachmentId: meta.id,
        filename: meta.filename,
        content: text,
        model: this.props.currentModel?.name
      });
    } catch (error) {
      console.error('Failed to read file:', error);
      alert('Failed to read file content.');
    }
  }

  @action
  private _onToggleImageGenMode = () => {
    this._isImageGenMode = !this._isImageGenMode;
    localStorage.setItem('isImageGenMode', this._isImageGenMode ? 'true' : 'false');
    if (this._isImageGenMode) {
      // Reset to defaults when entering image gen mode. Layout and size are
      // restored from storage directly (no setters) so the 'as-image' pair
      // stays consistent.
      this._imageLayout = localStorage.getItem('imageLayout') as ImageLayout || 'square';
      const storedSize = localStorage.getItem('imageSize') as ImageSizeChoice;
      const validSizes: ImageSizeChoice[] = ['1024', '1536', '2048', 'as-image'];
      this._imageSize = (storedSize && validSizes.includes(storedSize) ? storedSize : '1024') as ImageSizeChoice;
      this._imageGenModel = localStorage.getItem('imageGenModel') as ImageGenModel || 'zImage';
      this._selectedStyles = PromptArea._loadStoredStyles();
      this._setImageGenControlsMounted(true);
      // Refetch estimates when entering image gen mode (VRAM may have changed)
      this._fetchImageEstimates();
    } else {
      this._setImageGenControlsMounted(false);
      this._imageEstimates = null;
    }
    this.props.onImageGenModeChange?.(this._isImageGenMode);
  }

  /** Mounts/unmounts the image-gen controls. Unmount is delayed past the 0.28s
   *  collapse animation so the panel still animates closed before the preview
   *  images are removed from the DOM. */
  @action
  private _setImageGenControlsMounted(mounted: boolean) {
    if (this._imageGenUnmountTimer !== null) {
      clearTimeout(this._imageGenUnmountTimer);
      this._imageGenUnmountTimer = null;
    }
    if (mounted) {
      this._imageGenControlsMounted = true;
    } else if (this._imageGenControlsMounted) {
      this._imageGenUnmountTimer = setTimeout(() => {
        this._imageGenControlsMounted = false;
        this._imageGenUnmountTimer = null;
      }, 300);
    } else {
      this._imageGenControlsMounted = false;
    }
  }

  /** Fetch VRAM estimates for all ComfyUI image models from the server. */
  @action.bound
  private async _fetchImageEstimates(): Promise<void> {
    const data = await apiService.get<ImageEstimatesApiResponse>('/api/image/estimate');
    this._imageEstimates = data;
    this._imageEstimatesFetchedAt = Date.now();
  }

  @action.bound
  private _toggleImageGenControls = () => {
    this._imageGenControlsExpanded = !this._imageGenControlsExpanded;
    localStorage.setItem('imageGenControlsExpanded', this._imageGenControlsExpanded ? 'true' : 'false');
  }

  @action.bound
  private _setImageLayout = (layout: ImageLayout) => {
    // 'as-image' needs an attached reference image; reject it otherwise so the
    // picker can't lock onto a layout that has no image to follow.
    if (layout === 'as-image' && this._imageMetas.length === 0) return;
    if (layout === this._imageLayout) return;
    this._imageLayout = layout;
    localStorage.setItem('imageLayout', layout);
    // 'as-image' layout and size are a locked pair: selecting the layout follows
    // with the size, and leaving the layout (while the size is still 'as-image')
    // resets the size to the default tier.
    if (layout === 'as-image') {
      if (this._imageSize !== 'as-image') this._setImageSize('as-image');
    } else if (this._imageSize === 'as-image') {
      this._setImageSize('1024');
    }
  }

  @action.bound
  private _setImageSize = (size: ImageSizeChoice) => {
    // Hard guard: guests can't select the 1.5K/2K tiers (the UI also disables them).
    if ((size === '1536' || size === '2048') && (window.user?.role ?? 'guest') === 'guest') size = '1024';
    if (size === this._imageSize) return;
    this._imageSize = size;
    localStorage.setItem('imageSize', size);
    // Locked pair with the layout: 'as-image' size follows with the layout, and
    // leaving it (while the layout is still 'as-image') resets the layout to square.
    if (size === 'as-image') {
      if (this._imageLayout !== 'as-image') this._setImageLayout('as-image');
    } else if (this._imageLayout === 'as-image') {
      this._setImageLayout('square');
    }
  }

  @action.bound
  private _setImageGenModel = (model: ImageGenModel) => {
    const i2iModels: ImageGenModel[] = ['Flux.Kontext', 'Flux2.Dev', 'Flux2.Klein', 'qwen', 'qwen2.1', 'qwen2.1t', 'krea2'];
    if (i2iModels.includes(model)) {
      localStorage.setItem('lastImageEditModel', model);
    } else if (this._imageMetas.length > 0) {
      // Non-i2i model selected while images are attached → keep on last used i2i
      const last = localStorage.getItem('lastImageEditModel') as ImageGenModel | null;
      model = last || 'Flux2.Klein';
    }
    this._imageGenModel = model;
    localStorage.setItem('imageGenModel', model);
  }

  @action.bound
  private _setSelectedStyles = (styles: ImageStyleSelection[]) => {
    this._selectedStyles = styles;
    localStorage.setItem('imageGenStyles', JSON.stringify(styles));
    // Clear stale suggestion since the selected set changed
    this._keywordSuggestion = null;
    if (this._keywordSuggestionTimer !== null) {
      clearTimeout(this._keywordSuggestionTimer);
      this._keywordSuggestionTimer = null;
    }
  }

  @action.bound
  private _setVideoGenModel = (model: VideoGenModel) => {
    this._videoGenModel = model;
    localStorage.setItem('videoGenModel', model);
  }

  @action.bound
  private _setVideoSize = (size: number) => {
    // Hard guard: guests can't select 640p/720p (the UI also disables them).
    if ((size === 640 || size === 720) && (window.user?.role ?? 'guest') === 'guest') size = 480;
    this._videoSize = size;
    localStorage.setItem('videoSize', String(size));
  }

  @action.bound
  private _setVideoFps = (fps: number) => {
    this._videoFps = fps;
    localStorage.setItem('videoFps', String(fps));
  }

  @action.bound
  private _setVideoLength = (length: number) => {
    this._videoLength = length;
    localStorage.setItem('videoLength', String(length));
  }

  @action.bound
  private _setImageMode = () => {
    this._isVideoGenMode = false;
    localStorage.setItem('isVideoGenMode', 'false');
  }

  @action.bound
  private _setVideoMode = () => {
    this._isVideoGenMode = true;
    localStorage.setItem('isVideoGenMode', 'true');
  }

  public focus() {
    this._textareaRef.current?.focus();
  }

  /** Set the prompt text programmatically (e.g. when editing a queued steering
   *  message back into the editor) and focus the textarea. */
  @action.bound
  public setInputValue(text: string) {
    this.inputValue = text;
    this.save();
    this._textareaRef.current?.focus();
    setTimeout(this._recalculateHeight, 0);
  }

  /** Stage PWA-shared content into the composer; nothing is sent until the user does. */
  @action.bound
  public async processShareFiles(files: File[], text: string): Promise<void> {
    for (const file of files) {
      if (imageService.isImageFile(file)) {
        await this._processImageFile(file);
      } else if (imageService.isFileSupported(file) && imageService.validateAttachmentFile(file)) {
        const meta = await this._uploadFile(file);
        if (meta) this._attachmentMetas.push(meta);
      }
    }
    if (text) {
      this.inputValue = text;
      this.save();
    }
    this._textareaRef.current?.focus();
    setTimeout(this._recalculateHeight, 0);
  }

  @action.bound
  private _onTranscriptionDone(text: string) {
    if (!text) return;
    this.inputValue += text;
    this.save();
    setTimeout(this._recalculateHeight, 0);
    // start request automatically after transcription
    this._sendMessage();
  }

  @action.bound
  private _onStartRecording = () => {
    this._prefetchModel?.();
  }

  @action.bound
  private _onRecordingChange = (isRecording: boolean) => {
    this._isVoiceRecording = isRecording;
  }

  @action.bound
  private async _generateMagicPrompt() {
    // call server /api/magic-prompt with selected text, pass current model, layout and styles
    const body = {
      llmModel: this.props.currentModel?.name || 'gpt-oss:20b',
      imageModel: this._imageGenModel,
      layout: this._imageLayout,
      styles: this._selectedStyles,
      prompt: this.inputValue.trim(),
      attachmentIds: this._imageMetas.map(m => m.id)
    }

    const data = await apiService.post<{ prompt: string }>('/api/magic-prompt', body, {
      loader: this._generatePromptCompletion
    });

    // update textarea with new prompt
    await this._sendMessageInternal(data.prompt);
  }

  @action
  /** Map the LLM's free-form `type` arg (generate_image schema lists "SDXL", "Flux",
   *  "Flux2.Dev", "Flux2.Klein", "Qwen", "Qwen2.1", "ZImage", "Krea2") to a composer
   *  ImageGenModel, or null when unrecognized. */
  private static _normalizeImageGenModel(type: string | undefined): ImageGenModel | null {
    switch ((type ?? '').trim().toLowerCase()) {
      case 'sdxl': return 'SDXL';
      case 'flux':
      case 'flux1':
      case 'flux1.dev':
      case 'flux.1 dev': return 'Flux1.Dev';
      case 'flux.kontext':
      case 'flux-kontext': return 'Flux.Kontext';
      case 'flux2':
      case 'flux2.dev': return 'Flux2.Dev';
      case 'flux2.klein': return 'Flux2.Klein';
      case 'qwen':
      case 'qwen image': return 'qwen';
      case 'qwen2.1':
      case 'qwen 2.1': return 'qwen2.1';
      case 'qwen2.1t':
      case 'qwen 2.1 t': return 'qwen2.1t';
      case 'zimage':
      case 'z-image': return 'zImage';
      case 'krea2':
      case 'krea 2': return 'krea2';
      default: return null;
    }
  }

  /** Accepts the reference-image-ids argument as an array (normal) or a JSON-encoded
   *  array string (some producers serialize it). generate_image carries the IDs under
   *  'references' (LLM tool call) OR 'attachmentIds' (image-gen UI) — the server merges
   *  both (toolsComfyUiService), so we do the same. Returns clean non-empty IDs. */
  private static _parseReferenceIds(args: { references?: string[] | string; attachmentIds?: string[] | string }): string[] {
    const raw = args.references ?? args.attachmentIds ?? [];
    let ids: unknown[];
    if (Array.isArray(raw)) {
      ids = raw;
    } else if (typeof raw === 'string' && raw.startsWith('[')) {
      ids = JSON.parse(raw);
    } else {
      return [];
    }
    return ids.filter((id): id is string => typeof id === 'string' && id.length > 0);
  }

  /** Restore a past generate_image / generate_video tool call's parameters into the
   *  composer so the user can tweak them and re-run ("Use config"). */
  public async setImageSettings(settings: { prompt?: string; promptPositive?: string; orientation?: ImageLayout; type?: string; styles?: (string | ImageStyleSelection)[]; model?: string; size?: number; fps?: number; length?: number; imageId?: string; references?: string[]; attachmentIds?: string[]; needsImage?: boolean }, isVideo: boolean = false) {
    if (isVideo) {
      // generate_video tool args: prompt, model, imageId, orientation, size, fps, length
      if (settings.orientation) {
        this._setImageLayout(settings.orientation);
      }
      if (settings.model) {
        this._setVideoGenModel(settings.model as VideoGenModel);
      }
      if (typeof settings.size === 'number') {
        this._setVideoSize(settings.size);
      }
      if (typeof settings.fps === 'number') {
        this._setVideoFps(settings.fps);
      }
      if (typeof settings.length === 'number') {
        this._setVideoLength(settings.length);
      }
      if (settings.prompt) {
        this.inputValue = settings.prompt;
      }

      this._isImageGenMode = true;
      this._setVideoMode();
      // Expand the controls so the restored parameters are visible and editable.
      this._imageGenControlsExpanded = true;
      localStorage.setItem('imageGenControlsExpanded', 'true');
      this._setImageGenControlsMounted(true);
      localStorage.setItem('isImageGenMode', 'true');
      // Restore the reference image (i2v). Video mode is already set, so
      // addImageFromAttachmentId skips its auto 'as-image' layout.
      if (settings.imageId) {
        await this.addImageFromAttachmentId(settings.imageId);
      }
      // Hint only when no reference image was restored.
      this._imageRequiredHint = !!settings.needsImage && !settings.imageId;
      return;
    }

    // generate_image tool args (LLM: type/promptPositive/references; UI: type/prompt/attachmentIds)
    const references = PromptArea._parseReferenceIds(settings);
    const model = PromptArea._normalizeImageGenModel(settings.type);
    if (model) {
      this._setImageGenModel(model);
    } else if (references.length > 0) {
      // References present but no recognizable type — the server routes i2i
      // requests to Flux.Kontext by default, so restore that model.
      this._setImageGenModel('Flux.Kontext');
    }
    if (settings.styles) {
      this._setSelectedStyles(normalizeStyleSelections(settings.styles));
    }
    const prompt = settings.prompt ?? settings.promptPositive;
    if (prompt) {
      this.inputValue = prompt;
    }

    this._isImageGenMode = true;
    this._setImageGenControlsMounted(true);
    localStorage.setItem('isImageGenMode', 'true');
    // Restore reference images (i2i). Each add increments the attachment's refcount;
    // addImageFromAttachmentId auto-switches to an i2i model when the current one
    // doesn't accept references. Awaited sequentially so the per-model max-images
    // guard sees each attachment as it lands.
    for (const id of references) {
      await this.addImageFromAttachmentId(id);
    }
    // Restore the explicit orientation last: adding a reference auto-selects
    // 'as-image', and the LLM's explicit choice (if any) should win.
    if (settings.orientation) {
      this._setImageLayout(settings.orientation);
    }
    // Hint only when no reference image was restored.
    this._imageRequiredHint = !!settings.needsImage && references.length === 0;
  }

  /** Returns missing lora keywords (comma-separated) that should be added to the prompt, or null if nothing is missing */
  private _computeKeywordSuggestion(): string | null {
    if (!this._isImageGenMode || this._isVideoGenMode || this._selectedStyles.length === 0) return null;
    const prompt = this.inputValue.toLowerCase().trim();
    if (!prompt) return null;
    const missing: string[] = [];
    for (const styleSel of this._selectedStyles) {
      const parsed = parseLoraStyleName(styleSel.name);
      if (!parsed.keywords || parsed.keywords.length === 0) continue;
      const hasKeyword = parsed.keywords.some(kw => prompt.includes(kw.toLowerCase()));
      if (!hasKeyword) {
        missing.push(parsed.keywords[0]);
      }
    }
    return missing.length > 0 ? missing.join(', ') : null;
  }

  @action
  private _acceptKeywordSuggestion = () => {
    if (!this._keywordSuggestion) return;
    this.inputValue = this.inputValue.trimEnd() + ', ' + this._keywordSuggestion;
    this._keywordSuggestion = null;
    this.save();
    setTimeout(this._recalculateHeight, 0);
    this._textareaRef.current?.focus();
  }

  private _showMaxImagesError() {
    const now = Date.now();
    if (now - this._maxImagesErrorShownAt < 1000) return;
    this._maxImagesErrorShownAt = now;
    modalService.showError(
      `This model supports up to ${this._maxImages} reference image${this._maxImages === 1 ? '' : 's'}. Remove an existing image to add a new one.`,
      'Maximum images reached'
    );
  }

  @action
  private _dismissKeywordSuggestion = () => {
    this._keywordSuggestion = null;
    if (this._keywordSuggestionTimer !== null) {
      clearTimeout(this._keywordSuggestionTimer);
      this._keywordSuggestionTimer = null;
    }
    this._textareaRef.current?.focus();
  }

  private _formatSize(size: number) {
    const sizeStr = size < 1024
      ? `${size} B`
      : size < 1024 * 1024
        ? `${(size / 1024).toFixed(1)} KB`
        : `${(size / (1024 * 1024)).toFixed(1)} MB`;

    return sizeStr;
  }

}

export default PromptArea;